import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, RefreshCw, Trash2, ExternalLink, Newspaper } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminNewsManager() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [form, setForm] = useState({ title: '', summary: '', link: '', thumbnail: '', source_tag: 'Local' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!user.is_admin) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchNews();
  }, [user, navigate]);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/news/feed?limit=50`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setItems(d.items || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshRSS = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API}/news/refresh`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Refreshed: +${d.pro_inserted || 0} pro, +${d.dmv_inserted || 0} events`);
        fetchNews();
      } else {
        toast.error('Refresh failed');
      }
    } finally {
      setRefreshing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || form.title.trim().length < 2) {
      toast.error('Title is required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/news/dmv`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title.trim(),
          summary: form.summary.trim(),
          link: form.link.trim() || null,
          thumbnail: form.thumbnail.trim() || null,
          source_tag: form.source_tag.trim() || 'Local',
        }),
      });
      if (res.ok) {
        toast.success('DMV headline posted');
        setForm({ title: '', summary: '', link: '', thumbnail: '', source_tag: 'Local' });
        fetchNews();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to post');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const deleteItem = async (newsId) => {
    if (!window.confirm('Delete this news item?')) return;
    const res = await fetch(`${API}/news/${newsId}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) {
      toast.success('Deleted');
      setItems(items.filter(i => i.news_id !== newsId));
    } else {
      toast.error('Delete failed');
    }
  };

  const tagColor = (cat) => ({
    pro: 'bg-blue-100 text-blue-700',
    dmv: 'bg-emerald-100 text-emerald-700',
    bundle: 'bg-amber-100 text-amber-700',
  }[cat] || 'bg-gray-100 text-gray-600');

  return (
    <div className="min-h-screen bg-bundle-cream">
      <div className="max-w-3xl mx-auto p-4 pb-mobile-nav">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin')}
            data-testid="admin-news-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-heading font-bold text-bundle-green flex items-center gap-2">
            <Newspaper className="w-6 h-6" /> News Manager
          </h1>
          <Button
            onClick={refreshRSS}
            disabled={refreshing}
            size="sm"
            className="ml-auto bg-bundle-green hover:bg-bundle-green-dark text-white"
            data-testid="admin-news-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh RSS'}
          </Button>
        </div>

        {/* New DMV post form */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">📍 Post a DMV Headline</CardTitle>
            <p className="text-xs text-gray-500">Manually publish a local-golf headline. Shows up on every member's Dashboard under "Bundle News".</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label htmlFor="title">Headline *</Label>
                <Input
                  id="title"
                  data-testid="admin-news-title-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Renditions Spring Open registration is open"
                  maxLength={200}
                  required
                />
              </div>
              <div>
                <Label htmlFor="summary">Summary</Label>
                <Input
                  id="summary"
                  data-testid="admin-news-summary-input"
                  value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  placeholder="One-line description that appears below the headline"
                  maxLength={400}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="link">Link (optional)</Label>
                  <Input
                    id="link"
                    data-testid="admin-news-link-input"
                    value={form.link}
                    onChange={(e) => setForm({ ...form, link: e.target.value })}
                    placeholder="https://... or /events?id=..."
                  />
                </div>
                <div>
                  <Label htmlFor="tag">Tag</Label>
                  <Input
                    id="tag"
                    data-testid="admin-news-tag-input"
                    value={form.source_tag}
                    onChange={(e) => setForm({ ...form, source_tag: e.target.value })}
                    placeholder="Local"
                    maxLength={40}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="thumb">Thumbnail URL (optional)</Label>
                <Input
                  id="thumb"
                  data-testid="admin-news-thumb-input"
                  value={form.thumbnail}
                  onChange={(e) => setForm({ ...form, thumbnail: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="admin-news-submit-btn"
                className="bg-bundle-green hover:bg-bundle-green-dark text-white"
              >
                {submitting ? 'Posting...' : 'Post Headline'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Existing items */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Headlines ({items.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-gray-500 py-4">Loading…</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-gray-500 py-4">No headlines yet — post one above or hit "Refresh RSS".</p>
            ) : (
              <div className="space-y-2">
                {items.map(item => (
                  <div
                    key={item.news_id}
                    className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                    data-testid={`admin-news-row-${item.news_id}`}
                  >
                    {item.thumbnail && (
                      <img src={item.thumbnail} alt="" className="w-16 h-16 rounded object-cover flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${tagColor(item.category)}`}>
                          {item.category}
                        </span>
                        <span className="text-[10px] text-gray-500">{item.source}</span>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 line-clamp-2">{item.title}</p>
                      {item.summary && <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{item.summary}</p>}
                      {item.link && (
                        <a
                          href={item.link}
                          target={item.external ? '_blank' : '_self'}
                          rel="noopener noreferrer"
                          className="text-[11px] text-bundle-green hover:underline inline-flex items-center gap-1 mt-1"
                        >
                          <ExternalLink className="w-3 h-3" /> Open link
                        </a>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteItem(item.news_id)}
                      data-testid={`admin-news-delete-${item.news_id}`}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
